import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import { useAuth } from '../../contexts/AuthContext';

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
];

const ChangeLanguage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Set current language from i18n or user profile
    const lang = i18n.language.split('-')[0];
    setCurrentLanguage(user?.language || lang || 'en');
  }, [user]);

  const handleLanguageChange = async (languageCode: string) => {
    if (languageCode === currentLanguage) return;

    setIsLoading(true);
    try {
      // Change language immediately for better UX
      await i18n.changeLanguage(languageCode);
      localStorage.setItem('userLanguage', languageCode);

      // Save to backend
      await axios.put('/users/settings', { language: languageCode });

      // Update user in localStorage
      if (user) {
        const updatedUser = { ...user, language: languageCode };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      setCurrentLanguage(languageCode);
      toast.success(t('success.saved'));
      navigate('/profile');
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('errors.unknown'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-light-surface dark:hover:bg-dark-surface"
            >
              <ArrowLeftIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
            </button>
            <h1 className="ml-4 text-xl font-semibold text-light-text dark:text-dark-text">
              {t('settings.language')}
            </h1>
          </div>
        </div>

        {/* Language List */}
        <div className="flex-1 overflow-y-auto">
          <div className="py-2">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                disabled={isLoading}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-light-surface dark:hover:bg-dark-surface transition-colors disabled:opacity-50"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{language.flag}</span>
                  <span className="text-light-text dark:text-dark-text">{language.name}</span>
                </div>
                {currentLanguage === language.code && (
                  <CheckIcon className="w-5 h-5 text-primary-500" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default ChangeLanguage;
